import axios from "axios";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ImageCompressor from "../../../utilities/ImageCompressor";
import ImagePreview from "./ImagePreview";
import ImageUpload from "./ImageUpload";

const styles = {
    container: {
        padding: "20px",
        fontFamily: "Arial, sans-serif",
    },
    title: {
        fontSize: "18px",
        fontWeight: "700",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: "2rem",
    },
    imageContainer: {
        position: "relative",
        width: "100%",
        height: "35vh",
        overflow: "hidden",
        borderRadius: "30px",
        backgroundColor: "#fff",
    },
    image: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    addButton: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "35vh",
        backgroundColor: "#fff",
        boxShadow: "0px 8px 20px 0px rgba(0, 0, 0, 0.08)",
        borderRadius: "10px",
        cursor: "pointer",
    },
    notes: {
        marginTop: "10px",
        fontSize: "14px",
        color: "#666",
    },
    editButton: {
        border: "1px solid #5D5DFF",
        cursor: "pointer",
        padding: "0.3rem 1rem",
        fontSize: "14px",
        fontWeight: "600",
        borderRadius: "5px",
        color: "#5D5DFF",
    },
    deleteButton: {
        position: "absolute",
        bottom: "2rem",
        right: "2rem",
        background: "red",
        color: "white",
        border: "none",
        borderRadius: "20%",
        fontSize: "2rem",
        padding: "4px",
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    editActions: {
        display: "flex",
        alignItems: "center",
        gap: "0.8rem",
        cursor: "pointer",
    },
    btnDisabled: {
        cursor: "not-allowed",
        opacity: "0.6",
    },
    btnHover: {
        border: "1px solid #22226b",
    },
    loadingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 1,
    },
    spinner: {
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #3498db",
        borderRadius: "50%",
        width: "30px",
        height: "30px",
        animation: "spin 1s linear infinite",
    },
};

const InspectionSection = ({ field, inspectionItem, title, updateInspection, allData }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const textRef = useRef("");
    const [loading, setLoading] = useState(false);
    const [editingImages, setEditingImages] = useState(inspectionItem.images);
    // const [editingNotes, setEditingNotes] = useState(inspectionItem.notes);

    const exitEditingMode = () => {
        setIsEditMode(false);
        setEditingImages(inspectionItem.images);
        // setEditingNotes(inspectionItem.notes);
    };

    const enterEditingMode = () => {
        setIsEditMode(true);

        setEditingImages(inspectionItem.images);
        // setEditingNotes(inspectionItem.notes);
    };

    useEffect(() => {
        setEditingImages(inspectionItem.images);
        // setEditingNotes(inspectionItem.notes);
    }, [inspectionItem]);

    const handleSaveChanges = async () => {
        try {
            setLoading(true);

            const uploadImages = editingImages;
            const imageUploadPromises = uploadImages.map(async (image) => {
                if (typeof image === "string") {
                    return image;
                }
                if (!image.originFileObj) {
                    return image.url;
                }

                const imageData = new FormData();
                const compressedImage = await ImageCompressor(image.originFileObj);
                imageData.append("image", compressedImage);
                imageData.append("imagePath", "Cars");
                const resp = await axios.post(`${process.env.REACT_APP_SOFTCAR_OLD_API}/uploadSingleImage`, imageData);
                if (resp.status === 200) {
                    return resp.data?.url;
                }
                return null;
            });

            const imageURLArr = await Promise.all(imageUploadPromises);
            const filteredImageURLArr = imageURLArr.filter((url) => url !== null);

            const updatedData = {
                ...allData,
                values: {
                    ...(allData.values || {}),
                    inspection: {
                        ...allData.values.inspection,
                        [field]: {
                            ...allData.values.inspection[field],
                            notes: textRef.current.value,
                            images: filteredImageURLArr,
                        },
                    },
                },
            };
            // console.log("up", updatedData);

            textRef.current.value = "";

            exitEditingMode();
            await updateInspection(field, updatedData);
            setLoading(false);
        } catch (error) {
            // console.error(error);
            // console.error(error?.response);
            exitEditingMode();
            setLoading(false);
        }
    };

    const handleFileChange = useCallback((fieldParam, newFileList) => {
        setEditingImages(newFileList);
    }, []);

    // const handleNoteChange = useCallback((field, value) => {
    //     setEditingNotes(value);
    // }, []);

    // useEffect(() => {
    //     console.log("editingImages :", editingImages);
    // }, [editingImages]);

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1rem",
                }}>
                <h2 style={styles.title}>{title}</h2>
                <div style={{ display: "flex", gap: "10px" }}>
                    {isEditMode || loading ? (
                        <div style={{ ...styles.editButton }} onClick={handleSaveChanges}>
                            {loading ? "Saving..." : "Save Changes"}
                        </div>
                    ) : null}
                    <div style={styles.editButton} onClick={isEditMode ? exitEditingMode : enterEditingMode}>
                        {isEditMode || loading ? "Cancel" : "Edit"}
                    </div>
                </div>
            </div>
            {isEditMode ? (
                <ImageUpload
                    fileList={editingImages?.map((i) => (typeof i === "string" ? { url: i, thumbUrl: i } : i))}
                    handleFileChange={(newFileList) => handleFileChange(field, newFileList)}
                />
            ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    <ImagePreview imageList={inspectionItem.images} />
                </div>
            )}
            <div style={styles.grid}></div>
            <div style={{ padding: "1rem 0.2rem" }}>
                <h1 style={{ color: "#5D5DF6", fontSize: "16px" }}>Note</h1>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    {!isEditMode ? (
                        <textarea
                            ref={textRef}
                            value={inspectionItem.notes}
                            // value={inspectionItem.notes || 'No notes available!'}
                            // onChange={(e) => handleNoteChange(field, e.target.value)}
                            placeholder={!isEditMode ? "" : "Add your notes here..."}
                            style={{ width: "100%", minHeight: "30px", marginBottom: "10px" }}
                            readOnly={!isEditMode}
                        />
                    ) : (
                        <textarea
                            ref={textRef}
                            // value={editingNotes || 'No notes available!'}
                            // onChange={(e) => handleNoteChange(field, e.target.value)}
                            placeholder={!isEditMode ? "" : "Add your notes here..."}
                            style={{
                                width: "50%",
                                minHeight: "30px",
                                border: "1px solid #f7f7f5",
                                marginBottom: "10px",
                            }}
                            readOnly={!isEditMode}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

InspectionSection.propTypes = {
    field: PropTypes.string.isRequired,
    inspectionItem: PropTypes.shape({
        images: PropTypes.array,
        notes: PropTypes.string,
    }).isRequired,
    title: PropTypes.string.isRequired,
    updateInspection: PropTypes.func.isRequired,
    allData: PropTypes.shape({
        values: PropTypes.shape({
            inspection: PropTypes.object,
        }),
    }).isRequired,
};

export default InspectionSection;
